import React, { useMemo, useContext } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import {
  AppointmentStatus,
  AppointmentType,
  AppointmentWithThumbnail,
  OrgFeatures,
  Timezone,
} from '@bb/domain/dist/types';

import { useLink } from 'hooks';
import {
  formatTimeStr,
  featureEnabled,
  getInternalShowingDetailsUrl,
  getLocalTimeZone,
  correctShowingTypeLabel,
} from 'utils';
import { formatStatus } from '@app/helpers';
import { AuthContext } from 'contexts';
import Avatar from '../../Avatar';
import Icon from '../../Icon';
import Tag from '../../Tag';
import theme, { ColorNameType } from '../../theme';

import ListingImage from '../../ListingImage/ListingImage';

import AppointmentItemActions, {
  AppointmentPermission,
} from './AppointmentItemActions';

import type {
  ESTourItemResponse,
  VirtualTourWithThumbnail,
} from '../../../Router/pages/MyBusinessPage/types';

export const CANCELLED_STATUSES: AppointmentStatus[] = ['deny', 'cancel'];

type TAG_THEME_KEY = 'blue' | 'green' | 'gold' | 'red' | 'default';

type AppointmentListItemProps = {
  appointment: (
    | AppointmentWithThumbnail
    | VirtualTourWithThumbnail
    | ESTourItemResponse
  ) & { tourId?: string };
  isLastItem?: boolean;
  listingAppointmentPermissions?: { [i: string]: AppointmentPermission };
  isBuyer: boolean;
  team?: boolean;
};

export const ACTIONABLE_STATUSES: AppointmentStatus[] = [
  'new',
  'time_change',
  'attempted',
];

const renderAppointmentTime = (
  startTime: string | Date,
  endTime?: string,
  timeZone?: Timezone,
) => {
  let timeString = formatTimeStr(startTime, timeZone);
  timeString += endTime ? `-${formatTimeStr(endTime, timeZone)}` : '';
  return timeString;
};

const AppointmentItem: React.FC<AppointmentListItemProps> = ({
  appointment,
  isLastItem,
  isBuyer,
  team,
  listingAppointmentPermissions,
}) => {
  const { auth } = useContext(AuthContext);
  const linkTo = useLink();

  const {
    listing: { line1, line2, org: listingOrg },
    covid19RequestedAck,
    covid19Signed,
    status,
    listingContactTimeChange,
    type,
    isPending,
  } = appointment;

  const hasFeature = (feature: OrgFeatures) =>
    listingOrg?.features.includes(feature) ?? false;

  const {
    name: coopName = '',
    firstName,
    lastName,
    avatarFilestackId,
    org,
  } = (appointment as AppointmentWithThumbnail | VirtualTourWithThumbnail)
    .coop || {};

  // @TODO: Migrate to displayStatus field from API [ENG-12041]
  const buyerApptStatusText = useMemo(() => {
    if (status === 'time_change' && listingContactTimeChange) {
      return 'Suggested';
    }
    if (status === 'time_change') {
      return 'Time Change Request';
    }
    if (isPending && status !== 'confirm') {
      return 'Awaiting Approval';
    }
    if (status === 'confirm') {
      return 'Confirmed';
    }
    if (covid19RequestedAck && !covid19Signed) {
      return 'Awaiting Approval';
    }
    if (type && type === 'APPOINTMENT') {
      return 'Draft';
    }
    return formatStatus(status);
  }, [
    covid19RequestedAck,
    covid19Signed,
    status,
    listingContactTimeChange,
    type,
  ]);

  const buyerApptStatusColor: TAG_THEME_KEY = useMemo(() => {
    if (status === 'time_change' && listingContactTimeChange) {
      return 'default';
    }
    if (isPending) {
      return 'gold';
    }
    if (status === 'confirm') {
      return 'green';
    }
    if (type && type === 'APPOINTMENT') {
      return 'default';
    }
    if (['deny', 'cancel'].includes(status)) {
      return 'red';
    }
    return 'gold';
  }, [status, listingContactTimeChange, type]);

  // @TODO: Migrate to displayStatus field from API [ENG-12041]
  const listingApptStatusText = useMemo(() => {
    if (status === 'time_change' && listingContactTimeChange) {
      return 'Suggested';
    }
    if (status === 'time_change') {
      return 'Time Change Request';
    }
    if (isPending) {
      return 'Pending';
    }
    return formatStatus(status);
  }, [status, listingContactTimeChange]);

  const listingApptStatusColor: TAG_THEME_KEY = useMemo(() => {
    if (status === 'time_change' && listingContactTimeChange) {
      return 'default';
    }
    if (isPending) {
      return 'gold';
    }
    if (status === 'confirm') {
      return 'green';
    }
    if (status === 'new') {
      return 'blue';
    }
    if (['deny', 'cancel'].includes(status)) {
      return 'red';
    }
    return 'gold';
  }, [status, listingContactTimeChange]);

  const isCovidFlagEnabled = useMemo(
    () => appointment.orgHasCovidFlag,
    [appointment.orgHasCovidFlag],
  );
  return (
    <ItemContainer
      isLastItem={isLastItem}
      isBuyer={isBuyer}
      data-cy={isBuyer ? `manage-${appointment.id}` : ''}
    >
      <TagContainer>
        {isBuyer ? (
          <Tag
            data-cy={`appointment-status-${appointment.id}`}
            color={buyerApptStatusColor}
          >
            {buyerApptStatusText}
          </Tag>
        ) : (
          <Tag
            data-cy={`appointment-status-${appointment.id}`}
            color={listingApptStatusColor}
          >
            {listingApptStatusText}
          </Tag>
        )}
        {!isBuyer && !!appointment.type && (
          <TypeTag data-cy={`appointment-status-${appointment.id}`}>
            {correctShowingTypeLabel(appointment.type as AppointmentType)}
          </TypeTag>
        )}
      </TagContainer>
      <DetailsContainer
        data-cy={`appointment-details-container-${appointment.id}`}
        onClick={() => {
          if (!isBuyer) {
            let link = '';
            link += `${getInternalShowingDetailsUrl(
              appointment.id,
            )}?redirectTo=calendar&isListingAgent=true`;

            linkTo(link);
            return;
          }
          if (isBuyer) {
            let link = '';
            if (appointment.type === 'APPOINTMENT') {
              // clicking on draft appointments in tours should lead to edit tour page
              link += `/tours/${appointment.tourId}`;
            } else if (appointment.docType === 'virtualTour') {
              if (appointment.status === 'cancel') {
                return;
              }
              link += `/my_business/virtual_tour/${appointment.id}`;
            } else {
              link += `${getInternalShowingDetailsUrl(
                appointment.id,
              )}?redirectTo=calendar`;
            }
            linkTo(link);
          }
        }}
      >
        <div>
          <TimeLabel data-cy={`appointment-details-time-${appointment.id}`}>
            {appointment.time
              ? renderAppointmentTime(
                  appointment.time,
                  appointment.timeEnd,
                  getLocalTimeZone(auth),
                )
              : 'No time selected'}
          </TimeLabel>
          <GeneralLabel
            data-cy={`appointment-details-address${appointment.id}`}
            color={theme.gray8}
          >
            {line1} {line2 || ''}
          </GeneralLabel>
          {appointment.docType === 'virtualTour' &&
            hasFeature('VIRTUAL_SHOWINGS') && (
              <BottomLabelContainer>
                <Icon
                  type="videoCamera"
                  color="primary"
                  width={15}
                  height={14}
                />
                <GeneralLabel withIcon color={theme.gray7}>
                  Virtual Showing
                </GeneralLabel>
              </BottomLabelContainer>
            )}
          {/* TODO: Refactor me with Contacts component */}
          {!isBuyer && (
            <CoopContainer>
              <Avatar
                width={18}
                user={{
                  firstName: firstName || coopName.split(' ')[0] || '',
                  lastName: lastName || coopName.split(' ')[1] || '',
                  avatarFilestackId,
                }}
              />
              <div>
                <GeneralLabel
                  data-cy={`appointment-details-coop-name-${appointment.id}`}
                  withIcon
                  color={theme.gray8}
                >
                  <strong>{coopName}</strong>
                </GeneralLabel>
                {!!org?.name && (
                  <GeneralLabel
                    data-cy={`appointment-details-org-name-${appointment.id}`}
                    withIcon
                    color={theme.gray8}
                  >
                    {org.name}
                  </GeneralLabel>
                )}
              </div>
            </CoopContainer>
          )}
          {appointment.status === 'time_change' &&
            !!appointment.originalTime && (
              <GeneralLabel
                data-cy={`appointment-details-prev-time-${appointment.id}`}
                color={theme.gray7}
              >
                Previous: {moment(appointment.originalTime).format('ddd MMM D')}{' '}
                •{' '}
                {`${moment(appointment.originalTime).format(
                  'h:mma',
                )} - ${moment(appointment.originalTimeEnd).format('h:mma')}`}
              </GeneralLabel>
            )}
          {!CANCELLED_STATUSES.includes(appointment.status) &&
            covid19RequestedAck &&
            isCovidFlagEnabled && (
              <BottomLabelContainer>
                <Icon
                  type={covid19Signed ? 'checkCircle' : 'exclamationTriangle'}
                  color={
                    (covid19Signed ? theme.cyan6 : theme.gold6) as ColorNameType
                  }
                />
                <GeneralLabel
                  data-cy={`appointment-details-covid-${appointment.id}`}
                  withIcon
                  color={theme.gray8}
                >
                  COVID-19 Waiver {covid19Signed ? 'Signed' : 'Required'}
                </GeneralLabel>
              </BottomLabelContainer>
            )}
        </div>
        <ImageContainer
          data-cy={`appointment-details-listing-image-${appointment.id}`}
        >
          <ListingImage imageSrc={appointment.listing.thumbnail} />
        </ImageContainer>
      </DetailsContainer>
      {ACTIONABLE_STATUSES.includes(appointment.status) && !isBuyer && (
        <AppointmentItemActionsContainer>
          <AppointmentItemActions
            appointmentId={appointment.id}
            permission={listingAppointmentPermissions[appointment.id]}
            team={team}
            listingId={appointment.listing.id}
          />
        </AppointmentItemActionsContainer>
      )}
    </ItemContainer>
  );
};

export default AppointmentItem;

const ItemContainer = styled.div<{ isLastItem?: boolean; isBuyer?: boolean }>`
  margin-bottom: ${({ isLastItem }) => (isLastItem ? '0' : '16px')};
  padding-bottom: 10px;
  border-bottom: ${({ isLastItem }) =>
    isLastItem ? 'none' : '1px solid #e8e8e8'};

  ${({ isBuyer }) => (isBuyer ? `cursor: pointer;` : '')}
`;

const TagContainer = styled.div`
  display: flex;
  margin-bottom: 8px;
  align-items: center;
  padding: 4px;

  .ant-tag {
    padding: 1px 8px;
  }
`;

const TypeTag = styled.div`
  color: ${theme.gray8};
  font-size: 12px;
  line-height: 20px;
  margin-left: 8px;
`;

const DetailsContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TimeLabel = styled.div`
  color: ${theme.gray8};
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
`;

const BottomLabelContainer = styled.div`
  display: flex;
  margin-top: 4px;
  align-items: center;
  max-width: 0;
  overflow: visible;
  white-space: nowrap;
  svg {
    fill: none !important;
  }
`;

const GeneralLabel = styled.div<{ color: string; withIcon?: boolean }>`
  color: ${({ color }) => color};
  margin-left: ${({ withIcon }) => (withIcon ? '5px' : '0')};
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
`;

const CoopContainer = styled.div`
  display: flex;
  margin-top: 5px;

  .ant-avatar {
    min-width: 18px;
  }
`;

const ImageContainer = styled.div`
  padding: 8px;
`;

const AppointmentItemActionsContainer = styled.div`
  margin-top: 5px;
`;
